import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import authContext from '../../context/authContext/authContext';
import { updateAchievementAction } from '../../redux/actions/achievementsAction';
import { getUsersAction } from '../../redux/actions/userAction';
import RenderError from '../../utils/RenderError';
import { achievementOptions } from '../../utils/utils';

const dataState = {
  partnerId: '',
  event: '',
  achievement: ''
}

const errorState = {
  partnerError: '',
  eventError: '',
  achievementError: '',
  error: ''
}

const initialState = {
  ...dataState,
  ...errorState
};

const EditAchievement = ({ isOpen, toggle, getAchivements, achievements, achievementId, loading }) => {
  const dispatch = useDispatch();
  const { getConfig } = useContext(authContext);

  const [state, setCompleteState] = useState(initialState);
  const setState = (newState) => setCompleteState((prevState) => ({ ...prevState, ...newState }));

  const { loading: userLoading, users } = useSelector((state) => state.users);

  useEffect(() => {
    const achievement = achievements?.find((achievement) => achievement._id === achievementId);
    if (achievement) {
      const { userId: partnerId, event, achievement: achievementDetails } = achievement;
      setState({ partnerId, event, achievement: achievementDetails, data: achievement });
    }
  }, [achievementId])

  const getUsers = () => {
    const config = getConfig();
    const data = { partnersOnly: true };
    dispatch(getUsersAction(config, data));
  }

  useEffect(() => {
    if (isOpen) {
      getUsers()
    }
  }, [isOpen]);

  const onHideModal = () => {
    setState({ ...initialState });
    toggle();
  }

  const validatePartner = () => {
    if (!state.partnerId) {
      setState({ partnerError: '*Please select a partner' });
    }
  }

  const validateEvent = () => {
    if (!state.event.trim()) {
      setState({ eventError: '*Please enter a valid event' });
    }
  };

  const validateAchievement = () => {
    if (!state.achievement) {
      setState({ achievementError: '*Please select an achievement' });
    }
  };

  const editAchievement = async () => {
    const { partnerId: userId, event, achievement, data } = state;
    const config = getConfig();
    const update = {...data, userId, event, achievement };
    dispatch(updateAchievementAction(update, config, getAchivements, onHideModal));
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const { partnerId, event, achievement } = state;
    if (!partnerId || !event || !achievement) {
      setState({ error: '*Please fill all the required fields' });
      return;
    }

    editAchievement();
  }

  const closeBtn = () => <button className='btn btn-close' onClick={onHideModal}>X</button>;

  return (
    <Modal isOpen={isOpen} toggle={onHideModal}>
      <ModalHeader toggle={onHideModal} close={closeBtn()}>
        <div className='d-flex align-items-center'>
          <div>Edit Achievement</div>
          <div className='ml-3'>
            {loading ? <div className="spinner-border text-primary" role="status" /> : null}
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-md-12'>
              <div className="form-group">
                <label>Partner</label>
                <select
                  className="form-control"
                  value={state.partnerId}
                  onChange={(e) => setState({ partnerId: e.target.value, partnerError: '' })}
                  onBlur={validatePartner}
                  disabled={loading || userLoading}
                >
                  <option value="">Select Partner</option>
                  {users && users.map((user) => (
                    <option
                      data-user={user._id}
                      data-partner={state.partnerId}
                      selected={user._id === state.partnerId}
                      value={user._id}
                    >
                      {user.name} - {user.email}
                    </option>
                  ))}
                </select>
                <RenderError error={state.partnerError} />
              </div>
            </div>

            <div className='col-md-12'>
              <div className="form-group">
                <label>Event</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter event name"
                  value={state.event}
                  onChange={(e) => setState({ event: e.target.value, eventError: '' })}
                  onBlur={validateEvent}
                  disabled={loading}
                />
                <RenderError error={state.eventError} />
              </div>
            </div>

            <div className='col-md-12'>
              <div className="form-group">
                <label>Achievement</label>
                <select
                  class="form-control"
                  value={state?.achievement}
                  onChange={(e) => setState({ achievement: e.target.value })}
                  onFocus={() => setState({ achievementError: '' })}
                  onBlur={validateAchievement}
                  disabled={loading}
                >
                  {achievementOptions.map((option) => (
                    <option selected={option.value === state.achievement} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <RenderError error={state.achievementError} />
              </div>
            </div>
          </div>
          <div className='text-center'>
            <RenderError error={state.error} />
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onHideModal} disabled={loading}>
          Cancel
        </Button>
        <Button type='submit' color="gleamdesk-blue gleamdesk-orange" onClick={handleSubmit} disabled={loading}>
          Update
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default EditAchievement;